import React, {
  useContext,
  useState,
  Dispatch,
  useEffect,
  useCallback,
} from "react";
import { Group, Modal, Select, SimpleGrid, Space, Stack } from "@mantine/core";
import lodash from "lodash";
import { Navigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { AxiosError } from "axios";
import { AuthContext } from "../../../providers/AuthProvider";
import ErrorPage from "../../../common/pages/ErrorPage";
import Employee, {
  EmployeeListApiResponse,
} from "../../employees/services/model/Employee";
import MatchedEmployeePayslipBox from "../components/MatchedEmployeePayslipBox";
import MatchEmployeePayslipEmptyState from "../components/MatchEmployeePayslipEmptyState";
import PDFDocumentPreviewPanel from "../components/PDFDocumentPreviewPanel";
import UploadPayslipButton from "./UpdatePayslipButton";
import InputLabel from "../../../common/elements/InputLabel";
import Dictionary from "../../../utils/Dictionary";
import { AnalyticsContext } from "../../../providers/AnalyticsProvider";
import DocumentImporterConfiguration from "../services/model/DocumentImporterConfiguration";
import UnmatchedDocumentsResult from "../services/model/UnmatchedDocumentsResult";

type MatchPayslipModalProps = {
  id: number;
  jobId: number;
  employeeList?: EmployeeListApiResponse;
  setSearch: Dispatch<string>;
  opened: boolean;
  setOpened: Dispatch<boolean>;
  error: AxiosError | null;
  isError: boolean;
  data: UnmatchedDocumentsResult;
  documentImporterConfiguration: DocumentImporterConfiguration;
};

const MatchPayslipModal: React.FC<MatchPayslipModalProps> = ({
  id,
  jobId,
  employeeList,
  setSearch,
  opened,
  setOpened,
  error,
  isError,
  data,
  documentImporterConfiguration,
}) => {
  const auth = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { trackDocumentsEvent } = React.useContext(AnalyticsContext);
  const [status, setStatus] = useState("Unmatched");
  const [selectedEmployee, setSelectedEmployee] = useState<Employee>();

  useEffect(() => {
    trackDocumentsEvent(
      {
        event: "Matching_Modal",
        description: `User views the matching modal on the unmatched ${documentImporterConfiguration.document_name_plural_lower_case} dashboard`,
        event_type: "ModalView",
        job: data,
        type: documentImporterConfiguration.document_name_plural,
      },
      {
        notification_sent_status: data.should_send_notifications,
      }
    );
  }, [opened, trackDocumentsEvent, data, documentImporterConfiguration]);

  const onSuccess = useCallback((): void => {
    setOpened(false);
    setStatus("Unmatched");
    setSelectedEmployee(undefined);
  }, [setOpened]);

  const debouncedTrackDocumentsEvent = useCallback(
    lodash.debounce((value: string) => {
      if (value.length === 0) {
        return;
      }
      trackDocumentsEvent(
        {
          event: "Matching_Modal_Search",
          description: `User clicks and/or searches on the search bar on the unmatched ${documentImporterConfiguration.document_name_plural_lower_case} matching modal`,
          event_type: "Search_Bar_Input",
          job: data,
          type: documentImporterConfiguration.document_name_plural,
        },
        {
          search_input: value || "",
        }
      );
    }, 500),
    [trackDocumentsEvent, data, documentImporterConfiguration]
  );

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  return (
    <Modal
      size="50%"
      opened={opened}
      onClose={() => {
        trackDocumentsEvent({
          event: "Matching_Modal_Close",
          description: `User clicks on the X on the ${documentImporterConfiguration.document_name_plural_lower_case} matching modal`,
          event_type: "CTA:Click",
          job: data,
          type: documentImporterConfiguration.document_name_plural,
        });
        setOpened(false);
      }}
      title="Assign To Employee"
    >
      {isError ? (
        <ErrorPage errors={error} />
      ) : (
        <>
          <SimpleGrid cols={2}>
            <div>
              <PDFDocumentPreviewPanel
                id={id}
                onZoom={() => {
                  trackDocumentsEvent({
                    event: "Matching_Modal_Zoom",
                    description: `User clicks on the image in the matching modal to view zoomed in ${documentImporterConfiguration.document_name_lower_case}`,
                    event_type: "CTA:Click",
                    job: data,
                    type: documentImporterConfiguration.document_name_plural,
                  });
                }}
              />
            </div>
            <div>
              <Stack justify="flex-start">
                <Select
                  id="payslips-employee-select"
                  label={
                    <InputLabel
                      label={Dictionary.Payslips.select_mathing_employee}
                      tooltip={
                        Dictionary.Payslips.select_mathing_employee_tooltip
                      }
                    />
                  }
                  placeholder="Start typing to display names"
                  searchable
                  clearable
                  onSearchChange={async (value) => {
                    await queryClient.cancelQueries([
                      "EmployeeListApiService",
                      "employer",
                      "pagination",
                      "tsAndCsAccepted",
                      "search",
                      "job_id",
                    ]);
                    setSearch(value || "");
                    debouncedTrackDocumentsEvent(value || "");
                  }}
                  onChange={(value) => {
                    const temp = employeeList?.results.find((it) =>
                      Object.is(it.id, Number(value))
                    );
                    setSelectedEmployee(temp);
                  }}
                  data={
                    employeeList?.results && employeeList.results.length > 0
                      ? employeeList.results.map((it) => ({
                          value: `${it.id}`,
                          label: `${it.first_name} ${it.last_name}`,
                        }))
                      : []
                  }
                />

                {selectedEmployee ? (
                  <MatchedEmployeePayslipBox
                    data={selectedEmployee}
                    status={status}
                  />
                ) : (
                  <MatchEmployeePayslipEmptyState />
                )}
              </Stack>
            </div>
          </SimpleGrid>

          <Space h="xl" />
          <Space h="xl" />

          <Group position="apart">
            <div />
            <UploadPayslipButton
              data={{
                document_id: id,
                job_id: jobId,
                employee_id: selectedEmployee?.id || 0,
              }}
              isDisabled={!selectedEmployee}
              updateStatus={setStatus}
              onSuccess={onSuccess}
              documentImporterConfiguration={documentImporterConfiguration}
              job={data}
            />
          </Group>
        </>
      )}
    </Modal>
  );
};

export default MatchPayslipModal;
